import "./MaskedInput.scss";
import "cleave.js/dist/addons/cleave-phone.us";

import cx from "classnames";
import Cleave from "cleave.js/react";
import Tooltip from "components/Tooltip/Tooltip";
import type { ChangeEventHandler } from "react";
import React from "react";
import type { FieldErrors } from "react-hook-form";

import type { CleaveOptions } from "cleave.js/options";

import { MASK_REGISTRY, VALID_MASKS } from "./masks";

import type { MaskType } from "./masks";

// re-export MaskType for backward compatibility
export type { MaskType };

export type MaskedInputProps = {
  label?: string;
  name: string;
  errors?: FieldErrors;
  readOnly?: boolean;
  mask: MaskType;
  childAfter?: React.ReactNode;
  childBefore?: React.ReactNode;
  onChange?: (event: { target: { value: string; name: string } }) => void;
  prefix?: string;
  isFocused?: boolean;
  tooltip?: string;
  maxLength?: number; // for decimal masks, this is total digits (e.g., 11 for numeric(11,2))
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "maxLength">;

type CleaveRef = {
  element: HTMLInputElement;
} & InstanceType<typeof Cleave>;

/**
 * get Cleave.js options for a given mask type
 * uses the mask registry to fetch the appropriate configuration
 * @param maxLength - for decimal masks, total digits (e.g., 11 means numeric(11,2))
 */
const getMaskOptions = (
  maskType: MaskType,
  maxLength?: number,
): CleaveOptions => {
  // runtime type check - will throw an error if the mask type is not valid
  if (!VALID_MASKS.includes(maskType)) {
    throw new Error(`Invalid mask type: ${maskType}`);
  }

  const maskFactory = MASK_REGISTRY[maskType];
  const maskConfig = maskFactory(maxLength);

  return maskConfig.cleaveOptions;
};

/**
 * Get placeholder text for a given mask type
 * Uses the mask registry to fetch the appropriate placeholder
 */
const getPlaceholder = (maskType: MaskType): string => {
  const maskFactory = MASK_REGISTRY[maskType];
  const maskConfig = maskFactory();

  return maskConfig.placeholder;
};

const MaskedInput = React.forwardRef<CleaveRef, MaskedInputProps>(
  (
    {
      childAfter,
      childBefore,
      onBlur,
      onChange,
      readOnly,
      label,
      name,
      errors,
      mask,
      prefix,
      isFocused,
      tooltip,
      maxLength,
      ...rest
    },
    ref,
  ) => {
    const handleChange = (event: {
      target: CleaveOptions & HTMLInputElement & { rawValue: string };
    }) => {
      if (!onChange) return;

      const rawValue = event.target.rawValue;

      // use the mask's formatValue function to clean/transform the raw value
      const maskFactory = MASK_REGISTRY[mask];
      const maskConfig = maskFactory(maxLength);
      const formattedValue = maskConfig.formatValue(rawValue);

      onChange({
        target: {
          value: formattedValue,
          name: name,
        },
      });
    };

    // this is temp for now.. need to find balance between keepDirtyValues and onBlur
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (mask === "dollar" && e.target.value) {
        const numValue = parseFloat(e.target.value.replace(/[^0-9.-]+/g, ""));
        if (!isNaN(numValue)) {
          onChange?.({
            target: {
              value: numValue.toFixed(2),
              name: name,
            },
          });
        }
      }
      onBlur?.(e);
    };

    // filter out any non-standard props that cause warnings
    const filteredRest = { ...rest };
    // remove non-standard props that cause React DOM warnings
    delete (filteredRest as Record<string, unknown>).classNames;

    return (
      <div
        className={cx("input KaboodleMaskedInput", {
          "KaboodleMaskedInput--Error": errors && errors?.[name],
          "KaboodleMaskedInput--Disabled": readOnly,
          "input--focused": isFocused,
        })}
        data-mask={mask}
        title={errors && (errors[name]?.message as string)}
      >
        {label && (
          <div className="label-wrapper">
            <label htmlFor={name}>{label}</label>
            {tooltip && (
              <Tooltip content={tooltip}>
                <span className="material-icons">help_outline</span>
              </Tooltip>
            )}
          </div>
        )}
        <div
          className={cx("input-wrapper", {
            "input-wrapper--child-after": childAfter,
            "input-wrapper--child-before": childBefore || prefix,
          })}
        >
          {childBefore}
          {prefix && <span className="input-prefix">{prefix}</span>}
          {/* @ts-expect-error - Cleave type incompatibility with React 18 types */}
          <Cleave
            placeholder={getPlaceholder(mask)}
            ref={ref}
            name={name}
            onBlur={handleBlur}
            onChange={
              handleChange as unknown as ChangeEventHandler<HTMLInputElement>
            }
            options={getMaskOptions(mask, maxLength)}
            disabled={readOnly}
            {...filteredRest}
          />
          {childAfter}
        </div>
      </div>
    );
  },
);

MaskedInput.displayName = "MaskedInput";

export default MaskedInput;
